import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import type { Tables } from '../../types/supabase';
import { toast } from 'sonner';

interface UserState {
  user: Tables<'appUsers'> | null;
  loading: boolean;
  fetchUser: () => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  updateUser: (data: Partial<Tables<'appUsers'>>) => Promise<void>;
  clearUser: () => void;
}

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        loading: false,

        fetchUser: async () => {
          set({ loading: true });
          try {
            const {
              data: { user: authUser },
              error: authError,
            } = await supabase.auth.getUser();

            if (authError) throw authError;

            if (authUser) {
              const { data: appUser, error } = await supabase.from('appUsers').select('*').eq('id', authUser.id).single();

              if (error) throw error;

              set({ user: appUser, loading: false });
            } else {
              set({ user: null, loading: false });
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            toast.error('Failed to load user data');
            set({ user: null, loading: false });
          }
        },

        updateUser: async data => {
          const currentUser = get().user;
          if (!currentUser) return;

          try {
            const { data: updatedUser, error } = await supabase.from('appUsers').update(data).eq('id', currentUser.id).select().single();

            if (error) throw error;

            set({ user: updatedUser });
            toast.success('Successfully updated user data');
          } catch (error) {
            console.error('Error updating user data:', error);
            toast.error('Failed to update user data');
          }
        },

        clearUser: () => {
          // Clear user store
          set({ user: null, loading: false });
        },
      }),
      {
        name: 'user-storage',
        partialize: state => ({ user: state.user }),
      }
    )
  )
);
